import { routes } from "@/routes/utils";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetPredefinedTasksQuery,
  useDeletePredefinedTaskMutation,
} from "@/services";
import { useAppDispatch } from "@/redux/hooks";
import { openSnackbarAction } from "@/redux/slices";
import { useQueryClient } from "@tanstack/react-query";
import { NAME, CATEGORY, PRIORITY, TERM } from "./consts";

const useTaskConfig = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const [deletedRow, setDeletedRow] = useState<any>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [filterData, setFilterData] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState<number>(0); // Changed to match given code
  const [pageSize, setPageSize] = useState<number>(10); // Changed to match given code

  // API query parameters
  const queryParams = {
    page: pageNumber + 1, // Adjust to 1-based for API
    limit: pageSize,
    orderBy: "updatedAt",        // ✅ sort field
    orderDirection: "DESC",
    ...(filterData?.[CATEGORY] ? { taskCategory: filterData?.[CATEGORY] } : {}),
    ...(filterData?.[PRIORITY] ? { priority: filterData?.[PRIORITY] } : {}),
    ...(filterData?.[TERM] ? { taskTerm: filterData?.[TERM] } : {}),
    ...(filterData?.[NAME] ? { taskName: filterData?.[NAME] } : {}),
  };

  // API hooks
  const {
    data: tasksData,
    isLoading,
    error,
  } = useGetPredefinedTasksQuery(queryParams);

  const { mutateAsync: deleteTask, isPending: isDeleting } =
    useDeletePredefinedTaskMutation();

  // Define Table Columns
  const columns = [
    {
      accessorKey: "taskName",
      header: <div>Task Name</div>,
      cell: (row: any) => (
        <span
          className="text-primary font-normal cursor-pointer"
          onClick={() => onRowClick(row.row.original)}
        >
          {String(row?.row?.original?.taskName || "")}
        </span>
      ),
      size: 80,
    },
    {
      accessorKey: "taskCategory",
      header: <div>Task Category</div>,
      accessorFn: (row: any) => String(row?.taskCategoryData?.[0]?.name || ""),
      size: 80,
    },
    {
      accessorKey: "priority",
      header: <div>Priority</div>,
      accessorFn: (row: any) => String(row?.priority || ""),
      size: 80,
    },
    {
      accessorKey: "taskTerm",
      header: <div>Task Term</div>,
      accessorFn: (row: any) => String(row?.taskTermData?.[0]?.taskTerm || ""),
      size: 80,
    },
    {
      accessorKey: "taskDescription",
      header: <div>Comments</div>,
      accessorFn: (row: any) => String(row?.taskDescription || ""),
    },
    {
      accessorKey: "action",
      header: <div>Action</div>,
      cell: (row: any) => (
        <Trash2
          className="h-4 stroke-red-600 cursor-pointer"
          onClick={() => {
            setOpenDeleteDialog(true);
            setDeletedRow(row);
          }}
        />
      ),
    },
  ];

  // Extract data from the nested structure
  const tableData = tasksData?.data?.[0]?.records || [];
  const totalRecords = tasksData?.data?.[0]?.pagination?.[0]?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / pageSize);

  const onAdd = () => {
    navigate(`${routes.predefinedTask}/${routes.add}`);
  };

  const onRowClick = (task: any) => {
    if (task?._id) {
      navigate(`${routes.predefinedTask}/${task._id}/${routes.edit}`);
    } else {
      console.error("No task ID found:", task);
    }
  };

  const onConfirmDelete = async () => {
    if (deletedRow?.row?.original?._id) {
      try {
        await deleteTask(deletedRow.row.original._id);
        dispatch(
          openSnackbarAction({
            message: "Task deleted successfully!",
            severity: "success",
          })
        );
        queryClient.invalidateQueries({ queryKey: ["predefinedTasks"] });
      } catch (error: any) {
        console.error("Error deleting task:", error);
        dispatch(
          openSnackbarAction({
            message:
              error?.response?.data?.message ||
              "An error occurred while deleting the task",
            severity: "error",
          })
        );
      }
    }
    setOpenDeleteDialog(false);
    setDeletedRow(null);
  };

  const onCancelFilter = () => {
    setOpenFilter(false);
  };

  const onFilterBtnClick = () => {
    setOpenFilter(true);
  };

  const onFilterApply = (newFilterData: any) => {
    setFilterData(newFilterData);
    setPageNumber(0); // Reset to first page on filter apply, like given code
    setOpenFilter(false);
  };

  return {
    columns,
    onAdd,
    deletedRow,
    openDeleteDialog,
    setOpenDeleteDialog,
    onConfirmDelete,
    onCancelFilter,
    onFilterBtnClick,
    onRowClick,
    openFilter,
    filterData,
    setFilterData,
    onFilterApply,
    tasksData: tableData,
    pageNumber,
    pageSize,
    totalPages,
    totalRecords,
    setPageNumber,
    setPageSize,
    isLoading,
    error,
    isDeleting,
  };
};

export default useTaskConfig;